import { PhoneIcon, EnvelopeIcon, ChatBubbleLeftEllipsisIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const contactMethods = [
  {
    title: 'Live Chat',
    description: 'Get instant help from our support team',
    icon: ChatBubbleLeftEllipsisIcon,
    detail: 'Available 9 AM - 8 PM EST',
    action: 'Start Chat',
    recommended: true,
    responseTime: 'Under 2 minutes'
  },
  {
    title: 'Phone Support',
    description: 'Speak directly with a caring expert',
    icon: PhoneIcon,
    detail: '(989) 589-5385',
    action: 'Call Now',
    responseTime: 'Immediate'
  },
  {
    title: 'Email Support',
    description: 'Send us your questions anytime',
    icon: EnvelopeIcon,
    detail: 'support@seniorthrive.com',
    action: 'Send Email',
    responseTime: 'Within 4 hours'
  }
];

const supportPromises = [
  'Real humans, not bots - we care about your success',
  'Patient explanations - we\'ll repeat until you\'re confident',
  'No question is too small - we\'re here to help',
  'Follow-up support - we check that your issue is resolved'
];

const ContactSection = () => {
  return (
    <section className="py-16 bg-primary/5">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Still Need <span className="text-primary">Personal Help</span>?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Sometimes you need to talk to a real person. Our support team is trained specifically 
            in senior care and understands the unique challenges you might face.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          {contactMethods.map((method) => {
            const IconComponent = method.icon;
            
            return (
              <Card key={method.title} className={`hover:shadow-lg transition-shadow cursor-pointer group ${
                method.recommended ? 'ring-2 ring-primary/20 bg-primary/5' : ''
              }`}>
                <CardHeader className="text-center">
                  {method.recommended && (
                    <div className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full mb-3 mx-auto w-fit">
                      Recommended
                    </div>
                  )}
                  <IconComponent className={`h-8 w-8 mx-auto mb-3 ${
                    method.recommended ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                  <CardTitle className="text-lg">{method.title}</CardTitle>
                  <CardDescription>{method.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-sm font-medium text-foreground mb-2">
                    {method.detail}
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Response time: {method.responseTime}
                  </p>
                  <button className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                    method.recommended 
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}>
                    {method.action}
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="bg-card border rounded-lg p-8 max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Our Support Promise
            </h3>
            <p className="text-muted-foreground">
              When you reach out to us, here's what you can expect:
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {supportPromises.map((promise, index) => (
              <div key={index} className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">{promise}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t text-center">
            <div className="flex items-center justify-center text-sm text-muted-foreground">
              <ClockIcon className="h-4 w-4 mr-2" />
              Support Hours: Monday-Friday 9 AM - 8 PM EST, Saturday 10 AM - 6 PM EST
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;